import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, LayoutGrid, LayoutList, Calendar, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from '@/contexts/LocationContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import ComingSoon from '@/components/ComingSoon';
import { EventGuideCard, CompactEventCard } from '@/components/GuideCard';
import api from '@/lib/api';

const CATEGORIES = [
  { value: 'all', label: 'All Events' },
  { value: 'trending', label: 'Trending' },
  { value: 'craft_beer', label: 'Craft Beer' },
  { value: 'date_night', label: 'Date Night' },
  { value: 'live_music', label: 'Live Music' },
  { value: 'art', label: 'Art & Culture' },
  { value: 'food', label: 'Food & Dining' },
];

const EventBrowse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isGold } = useAuth();
  const { currentCity, currentNeighborhood } = useLocation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('date');
  
  const category = searchParams.get('category') || 'all';
  const filter = searchParams.get('filter');
  const neighborhoodName = currentCity?.display || currentNeighborhood?.name || 'Denver';

  useEffect(() => {
    fetchEvents();
  }, [category, currentCity]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const cityParam = currentCity?.city ? `&city=${currentCity.city}` : '';
      const response = await api.get(`/discover/events?limit=50${cityParam}`);
      let allEvents = response.data.events || response.data || [];
      if (category && category !== 'all') allEvents = allEvents.filter(e => e.category === category);
      setEvents(allEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events
    .filter(event =>
      event.title.toLowerCase().includes(search.toLowerCase()) ||
      event.venue_name.toLowerCase().includes(search.toLowerCase()) ||
      (event.description && event.description.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'price_low') return a.price - b.price;
      if (sortBy === 'price_high') return b.price - a.price;
      return new Date(a.date) - new Date(b.date);
    });

  const handleCategoryChange = (value) => {
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  const getCategoryTitle = () => {
    if (filter === 'hot') return 'Hot Tickets';
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.label || 'All Events';
  };

  const getCategoryDescription = () => {
    switch (category) {
      case 'trending':
        return `The hottest events happening in ${neighborhoodName} right now. These are the experiences everyone is talking about.`;
      case 'craft_beer':
        return `Discover Denver's finest craft breweries, tastings, and beer-centric events in ${neighborhoodName}.`;
      case 'date_night':
        return `Curated romantic experiences perfect for couples looking to explore ${neighborhoodName} together.`;
      case 'live_music':
        return 'Live performances, concerts, and musical experiences from local and touring artists.';
      case 'art':
        return `Gallery openings, art walks, and cultural experiences in ${neighborhoodName}.`;
      case 'food':
        return `Culinary adventures, pop-up dinners, and food festivals from ${neighborhoodName}'s best kitchens.`;
      default:
        return `Your complete guide to events and experiences in ${neighborhoodName}.`;
    }
  };

  return (
    <ConsumerLayout darkTheme>

      <section className="relative py-16 lg:py-20 bg-gradient-to-br from-indigo-900/50 via-purple-900/30 to-[#0f172a] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1766353846635-00a10ab84157?crop=entropy&cs=srgb&fm=jpg&q=85')] bg-cover bg-center" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              {filter === 'hot' ? (
                <Sparkles className="w-8 h-8 text-amber-400" />
              ) : (
                <Calendar className="w-8 h-8 text-indigo-400" />
              )}
              <Badge variant="secondary" className="bg-slate-800/80 text-slate-200 border-slate-700">
                {filteredEvents.length} Events
              </Badge>
            </div>
            
            <h1 className="headline-serif text-4xl lg:text-5xl font-bold text-slate-50 mb-4" data-testid="events-page-title">
              {getCategoryTitle()}
            </h1>
            
            <p className="text-lg text-slate-300 mb-8 max-w-2xl">
              {getCategoryDescription()}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search events, venues..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-12 h-12 bg-slate-800/50 border-slate-700 text-slate-50 placeholder:text-slate-400 focus:border-indigo-500"
                  data-testid="event-search-input"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b border-slate-700">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <Button
                key={cat.value}
                variant={category === cat.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleCategoryChange(cat.value)}
                className={category === cat.value 
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                  : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
                data-testid={`category-filter-${cat.value}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-slate-200" data-testid="sort-select">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="date" className="text-slate-200">Date</SelectItem>
                <SelectItem value="price_low" className="text-slate-200">Price: Low to High</SelectItem>
                <SelectItem value="price_high" className="text-slate-200">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex border border-slate-700 rounded-md">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('list')}
                className={`rounded-r-none ${viewMode === 'list' ? 'bg-slate-700' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid="view-mode-list"
              >
                <LayoutList className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('grid')}
                className={`rounded-l-none ${viewMode === 'grid' ? 'bg-slate-700' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid="view-mode-grid"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
          </div>
        ) : filteredEvents.length === 0 ? (
          search ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20 bg-slate-800/50 rounded-2xl border border-slate-700"
            >
              <Calendar className="w-16 h-16 text-slate-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-50 mb-2">No events found</h3>
              <p className="text-slate-400 mb-6">Try adjusting your search terms</p>
              <Button variant="outline" onClick={() => setSearch('')} className="border-slate-600 text-slate-300">
                Clear Search
              </Button>
            </motion.div>
          ) : (
            <ComingSoon
              neighborhoodName={neighborhoodName}
              neighborhoodSlug={currentNeighborhood?.slug || ''}
              type="events"
              showSuggestionCTA={true}
              showExploreOther={true}
            />
          )
        ) : viewMode === 'list' ? (
          <div className="space-y-6">
            {filteredEvents.map((event, index) => (
              <EventGuideCard key={event.id} event={event} index={index} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredEvents.map((event, index) => (
              <CompactEventCard key={event.id} event={event} index={index} />
            ))}
          </div>
        )}

        {filteredEvents.length > 0 && (
          <div className="mt-12 text-center">
            <p className="text-sm text-slate-400">
              Showing {filteredEvents.length} events in {getCategoryTitle()}
            </p>
          </div>
        )}
      </div>

    </ConsumerLayout>
  );
};

export default EventBrowse;
